import { useCallback, useMemo } from 'react'

import { useUnit } from 'effector-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasMassMessagesPermission } from '@/models/permissions.ts'

import BirthDayIcon from '../../icons/birthday.svg'
import ChevronDownIcon from '../../icons/chevron-down.svg'
import CollectionsIcon from '../../icons/collections.svg'
import EditIcon from '../../icons/edit.svg'
import MoreIcon from '../../icons/more.svg'
import PinIcon from '../../icons/pin.svg'
import SearchIcon from '../../icons/search.svg'
import StatisticIcon from '../../icons/statistic.svg'

interface Props {
  onClickSearch: () => void
  onClickLists: () => void
  onClickMore: () => void
  onClickMassMessagesStats: () => void
  onClickBirthdays: () => void
  onClickCollections: () => void
}

export const CollapsedSidebarActions = ({
  onClickLists,
  onClickSearch,
  onClickMore,
  onClickMassMessagesStats,
  onClickCollections,
  onClickBirthdays,
}: Props) => {
  const hasMassMessagesPermission = useUnit($hasMassMessagesPermission)
  const handleNewMassMessageClick = useCallback(() => {
    if (hasMassMessagesPermission) {
      openDialog({
        type: DIALOG_TYPE.NEW_MESSAGE,
        isPersistent: true,
      })
    } else {
      openDialog({
        type: DIALOG_TYPE.FANS_LIST_TO_START_CHAT_WITH,
      })
    }
  }, [hasMassMessagesPermission])
  /*const handleClickQueue = useCallback(() => {
       return null;
     }, []);*/

  const actions = useMemo(
    () => [
      {
        label: 'New Mass Message',
        icon: EditIcon,
        action: handleNewMassMessageClick,
      },
      { label: 'Search', icon: SearchIcon, action: onClickSearch },
      /*{ label: "Queue", icon: CalendarIcon, action: handleClickQueue },*/
      hasMassMessagesPermission && {
        label: 'Mass Message Statistic',
        icon: StatisticIcon,
        action: onClickMassMessagesStats,
      },
      {
        label: 'Collections',
        icon: CollectionsIcon,
        action: onClickCollections,
      },
      { label: 'Birthday', icon: BirthDayIcon, action: onClickBirthdays },
      { label: 'Lists', icon: PinIcon, action: onClickLists },
      { label: 'More', icon: MoreIcon, action: onClickMore },
    ],
    [
      handleNewMassMessageClick,
      onClickSearch,
      hasMassMessagesPermission,
      onClickMassMessagesStats,
      onClickCollections,
      onClickBirthdays,
      onClickLists,
      onClickMore,
    ],
  )

  return (
    <Popover>
      <PopoverTrigger className="outline-none">
        <div className="flex cursor-pointer flex-col gap-1">
          <div className="hover:bg-primary-50 dark:hover:bg-primary-900 flex h-9 w-9 items-center justify-center rounded-md">
            <MoreIcon className="rotate-90" />
          </div>
          <span className="text-xs font-medium">Action</span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        className="w-[300px] rounded-lg p-4 outline-none"
      >
        <p className="mb-4 text-[18px] font-semibold">Action</p>
        <div className="flex flex-col gap-3">
          {actions.map((item) => {
            if (!item) return null

            const { icon: Icon, action, label } = item

            return (
              <div
                className="group/item flex cursor-pointer items-center justify-between"
                key={label}
                onClick={action}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-secondary-50 text-title group-hover/item:text-primary-500 dark:bg-secondary-600 flex h-9 w-9 items-center justify-center rounded-md">
                    <Icon />
                  </div>
                  <p className="font-medium">{label}</p>
                </div>
                <div className="text-secondary-300 group-hover/item:text-primary-500 flex h-4 w-4 items-center justify-center">
                  <ChevronDownIcon className="-rotate-90" />
                </div>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
